import React, { useState, useEffect } from "react";
import Header from "./Header";
import { User, WorkLog } from "../types";
import { useOutletContext } from "react-router-dom";
import { apiClient } from "../utils/apiClient";

interface SearchFilters {
  start_date: string;
  end_date: string;
  user_id: string;
}

// APIレスポンスの型定義
interface PaginatedResponse {
  work_logs: WorkLog[];
  meta: {
    count: number; // 総件数
    page: number; // 現在のページ
    items: number; // 1ページの件数
    last: number; // 総ページ数
    next: number | null;
    prev: number | null;
    total_hours: number; // 全件の合計時間
  };
}

const WorkLogList = () => {
  const API_PREFIX = "/api";

  // --- コンテキストから現在のユーザー情報を取得 ---
  const currentUser = useOutletContext<User | null>();

  // --- State ---
  const [users, setUsers] = useState<User[]>([]);
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [loading, setLoading] = useState(false);

  // ページネーション用
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHours, setTotalHours] = useState(0);

  // --- 締め日判定ロジック ---
  const closingDateStr = currentUser?.current_closing_date || "";
  const isClosed = (workedOnStr: string) => {
    return Boolean(
      workedOnStr && closingDateStr && workedOnStr <= closingDateStr,
    );
  };

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (d: Date) => {
    return d
      .toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replaceAll("/", "-");
  };

  // --- State管理 ---
  // activeFilters を削除し、filters ひとつに統合
  const [filters, setFilters] = useState<SearchFilters>({
    start_date: formatDate(firstDay),
    end_date: formatDate(today),
    user_id: currentUser ? String(currentUser.id) : "",
  });

  // --- 初期フィルタ設定 ---
  useEffect(() => {
    if (currentUser && filters.user_id === "") {
      setFilters((prev) => ({ ...prev, user_id: String(currentUser.id) }));
    }
  }, [currentUser]);

  // --- ユーザー一覧取得 ---
  useEffect(() => {
    apiClient
      .get<User[]>(`${API_PREFIX}/users`)
      .then((data) => setUsers(data))
      .catch((err) => console.error("Users fetch error:", err));
  }, []);

  // 検索条件やページが変わったら再取得
  useEffect(() => {
    let ignore = false;

    const fetchWorkLogs = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        if (filters.start_date)
          queryParams.append("start_date", filters.start_date);
        if (filters.end_date) queryParams.append("end_date", filters.end_date);
        if (filters.user_id) queryParams.append("user_id", filters.user_id);

        // ページ番号
        queryParams.append("page", String(page));

        const data = await apiClient.get<PaginatedResponse>(
          `${API_PREFIX}/work_logs?${queryParams.toString()}`,
        );

        if (!ignore) {
          if (page === 1) {
            // 検索条件変更後や初回読み込み時は入れ替え
            setWorkLogs(data.work_logs);
            setTotalHours(data.meta.total_hours || 0);
          } else {
            // 「もっと読み込む」時は既存のリストに後ろから合体
            setWorkLogs((prev) => [...prev, ...data.work_logs]);
            if (
              data.meta.total_hours !== undefined &&
              data.meta.total_hours !== null
            ) {
              setTotalHours(data.meta.total_hours);
            }
          }
          setTotalPages(data.meta.last || 1);
        }
      } catch (err) {
        if (!ignore) {
          console.error("Search error:", err);
          setWorkLogs([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    fetchWorkLogs();

    // クリーンアップ関数：次のuseEffectが走る前に実行される
    return () => {
      ignore = true;
    };
  }, [filters, page]);

  // 入力変更ハンドラ
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    // 検索条件が変わったらリセット
    setPage(1);
    setWorkLogs([]);
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#fff" }}
    >
      <Header title="業務報告一覧" />

      <div className="limit-width px-4">
        {/* 検索条件エリア */}
        <div className="row align-items-end mb-4 bg-light p-3 rounded border">
          <div className="col-auto">
            <label className="form-label fw-bold small">開始日</label>
            <input
              type="date"
              name="start_date"
              className="form-control"
              value={filters.start_date}
              onChange={handleChange}
            />
          </div>
          <div className="col-auto align-self-center px-0">～</div>
          <div className="col-auto">
            <label className="form-label fw-bold small">終了日</label>
            <input
              type="date"
              name="end_date"
              className="form-control"
              value={filters.end_date}
              onChange={handleChange}
            />
          </div>
          <div className="col-auto">
            <label className="form-label fw-bold small">担当者</label>
            <select
              name="user_id"
              className="form-select"
              style={{ width: "350px" }}
              value={filters.user_id}
              onChange={handleChange}
            >
              <option value="">全ての担当者</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>

          {/* ローディング表示 */}
          {loading && (
            <div className="col-auto align-self-center text-muted small">
              <span className="spinner-border spinner-border-sm me-1"></span>
              読込中...
            </div>
          )}
        </div>

        {/* 一覧テーブル */}
        <div className="table-responsive">
          <table className="table table-striped table-hover border align-middle">
            <thead className="bg-primary text-white text-center">
              <tr>
                <th style={{ width: "10%" }}>カテゴリ</th>
                <th style={{ width: "13%" }}>プロジェクト名</th>
                <th style={{ width: "9%" }}>担当者</th>
                <th style={{ width: "4%" }}>Link</th>
                <th style={{ width: "36%" }}>作業メモ</th>
                <th style={{ width: "9%" }}>作業日</th>
                <th style={{ width: "4%" }}>時間</th>
                <th style={{ width: "9%" }}>更新日</th>
                <th style={{ width: "6%" }}>状態</th>
              </tr>
            </thead>
            {/* ローディング中は少し薄くする */}
            <tbody
              style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
            >
              {workLogs.length === 0 && !loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    データが見つかりません
                  </td>
                </tr>
              ) : (
                workLogs.map((log) => {
                  const closed = isClosed(log.worked_on);
                  return (
                    <tr
                      key={log.id}
                      className={closed ? "table-secondary text-muted" : ""}
                    >
                      <td>{log.project?.category?.name}</td>
                      <td>{log.project?.name}</td>
                      <td>{log.user?.name}</td>
                      <td className="text-center">
                        {log.backlog_url && (
                          <a
                            href={log.backlog_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={
                              closed ? "text-decoration-none opacity-50" : ""
                            }
                            title={log.backlog_url}
                          >
                            🔗
                          </a>
                        )}
                      </td>
                      <td className="text-start">{log.note}</td>
                      <td className="text-center">{log.worked_on}</td>
                      <td className="text-end pe-3">{log.hours}</td>
                      <td className="text-center small">
                        {new Date(log.updated_at).toLocaleString()}
                      </td>
                      <td className="text-center">
                        {closed && (
                          <span
                            className="badge bg-secondary shadow-sm text-nowrap px-2 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            🔒
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* --- ページネーションコントロール --- */}
        {workLogs.length > 0 && page < totalPages && (
          <div className="d-flex justify-content-center mt-4 mb-5">
            <button
              className="btn btn-primary btn-lg w-100 shadow-sm fw-bold"
              style={{ maxWidth: "400px" }}
              disabled={loading}
              onClick={() => setPage((p) => p + 1)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  読み込み中...
                </>
              ) : (
                <>もっと読み込む（残り {totalPages - page} ページ）</>
              )}
            </button>
          </div>
        )}

        {/* すべて読み込み終わった時の表示 */}
        {workLogs.length > 0 && page >= totalPages && totalPages > 1 && (
          <div className="text-center text-muted my-5">
            全てのデータを読み込みました
          </div>
        )}

        {/* 合計時間フッター */}
        <div className="d-flex justify-content-end align-items-baseline mt-3 mb-5 px-3">
          <span className="fw-bold me-3">作業時間合計 :</span>
          <span className="fs-3 fw-bold">{totalHours}</span>
          <span className="fw-bold ms-2">時間</span>
        </div>
      </div>
    </div>
  );
};

export default WorkLogList;
